import {
  CAIP2,
  Chain,
  Currency,
  CurrencyNotSupportedError,
} from "@paywithglide/glide-js";
import { base, solana, polygon, ham, sx } from "@paywithglide/glide-js/chains";
import { usdc, eth, sand, tn100x } from "@paywithglide/glide-js/currencies";
import { Theme } from "./theme";

export type AppConfig = {
  projectId: string;
  name: string;
  logoUrl: string;
  faviconUrl: string;
  chain?: Chain;
  currency?: Currency;
  withdrawChains?: Chain[];
  defaultDepositTokenDenomination?: "token" | "usd";
  payRecipient?: string;
  paymentMethods?: Array<
    | "wallet"
    | "external_wallet"
    | "transfer"
    | "coinbase"
    | "interac"
    | "fiat"
    | "onramper"
  >;
  theme?: Partial<Theme>;
  whitelabel?: boolean;
  showSupportedTokensMessage?: boolean;
  hideSettlementCopy?: boolean;
  tokenAmountDefault?: boolean;
  chainIds?: CAIP2[];
  excludeChainIds?: CAIP2[];
  excludeCurrencyTiers?: ("tier1" | "tier2" | "tier3")[];
  allowedPaymentCurrencies?: string[];
};

export const appConfigs = {
  ["demo"]: {
    projectId: "b3c2adae-a613-4465-939b-fe7323f84f6d", // tushar+glide-apps@walletkit.com
    name: "Demo",
    logoUrl: "https://static.paywithglide.xyz/glide_demo_logo.svg",
    faviconUrl: "https://paywithglide.xyz/static/favicon.png",
    chain: base,
    currency: usdc,
    payRecipient: "0xc6FfEB1298Eb33Da430d14e5Eb789256ec344625",
    showSupportedTokensMessage: false, // Hide message
    paymentMethods: [
      "wallet",
      "external_wallet",
      "transfer",
      "coinbase",
      "fiat",
      "interac",
      "coinbase_app",
      "onramper",
    ],
  },

  ["demo.solana"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "Demo",
    logoUrl: "https://static.paywithglide.xyz/glide_demo_logo.svg",
    faviconUrl: "https://paywithglide.xyz/static/favicon.png",
    chain: solana,
    currency: usdc,
    payRecipient: "2EFy1oBbHDMAYA31vRSbVWxiqaK37Erk3HybC4HFpb9A",
    showSupportedTokensMessage: true, // Show "100+ supported" message

    paymentMethods: [
      "wallet",
      "external_wallet",
      "transfer",
      "coinbase",
      "fiat",
      "interac",
    ],
  },

  ["bankr.bot"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "Bankr.bot",
    logoUrl: "https://static.paywithglide.xyz/bankr.png?t=1",
    faviconUrl:
      "https://wrpcd.net/cdn-cgi/imagedelivery/BXluQx4ige9GuW0Ia56BHw/b6c0ecda-d5cf-4862-ae40-5915d7144700/anim=false,fit=contain,f=auto,w=336",
    chain: base,
    currency: eth,
    theme: {
      modalBorderWidth: "1px",
      modalBorderRadius: "4px",

      buttonPrimaryForeground: "#ffffff",
      buttonPrimaryBackground: "#000000",
      buttonPrimaryBorderColor: "#000000",
      buttonPrimaryBorderWidth: "1px",
      buttonPrimaryBorderRadius: "4px",
      buttonPrimaryPadding: "8px 12px",

      buttonSecondaryBackground: "#f5f5f5",
      buttonSecondaryBorderColor: "#d1d1d1",
      buttonSecondaryBorderWidth: "1px",
      buttonSecondaryBorderRadius: "4px",
      buttonSecondaryPadding: "8px 12px",

      alertBackground: "#f5f5f5",
      alertBorderWidth: "1px",

      colorModalBackdrop: "rgba(0, 0, 0, 0.5)",
      colorModalBackground: "#ffffff",
      colorModalBorder: "#d1d1d1",
      colorTextPrimary: "#000000",
      colorTextSecondary: "#666666",
      colorPrimaryButtonBackground: "#000000",
      colorBackgroundSecondary: "#f5f5f5",
      colorAlertBackground: "#f5f5f5",
      colorAlertAccent: "#666666",
      colorAlertWarningBackground: "#f5f5f5",
      colorAlertWarningAccent: "#666666",
      colorAlertBorder: "#d1d1d1",

      appLogoHeight: "24px",
    },
  },

  ["d-sports.org"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "D-Sports",
    logoUrl:
      "https://d-sports.org/wp-content/uploads/2024/09/d-sports-header.png",
    faviconUrl: "https://logo.synthfinance.com/d-sports.org",
    chain: polygon,
    currency: usdc,
    payRecipient: "0x3a39e61192DE2EFd58A6E0DCC25388FA122EcF35",

    paymentMethods: [
      "wallet",
      "external_wallet",
      "transfer",
      "coinbase",
      "fiat",
    ],
  },

  ["sx.bet"]: {
    projectId: "b719f2fa-776e-4f41-a086-082ca251fb17", // tushar+glide-sx@walletkit.com
    name: "SX.bet",
    logoUrl: "https://i.imgur.com/N05zaas.png",
    faviconUrl: "https://i.imgur.com/VQgQMa4.png",
    chain: sx,
    currency: usdc,

    paymentMethods: [
      "wallet",
      "external_wallet",
      "transfer",
      "coinbase",
      "coinbase_app",
      "fiat",
      "interac",
    ],

    theme: {
      buttonPrimaryForeground: "#fff",
      buttonPrimaryBackground: "#4994ec",

      buttonSecondaryBackground: "#393b4a",

      colorModalBackdrop: "#23242a",
      colorModalBackground: "#292a33",
      colorModalBorder: "#3f4048",

      colorTextPrimary: "#ffffff",
      colorTextSecondary: "#bfc0c2",

      colorBackgroundSecondary: "#393b4a",

      colorAlertBackground: "#0a1317",
      colorAlertAccent: "#52a3db",

      colorAlertWarningBackground: "#181208",
      colorAlertWarningAccent: "#dd9c41",
    },
  },

  ["play.rails.xyz"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "Rails Play",
    logoUrl: "https://i.imgur.com/ZV5SXDJ.png",
    faviconUrl: "https://logo.synthfinance.com/rails.xyz",
    chain: base,
    currency: {
      id: "stablememe",
      name: "StableMEME",
      decimals: 18,
      symbol: "STABLE",
      logoUrl: "https://logo.synthfinance.com/stablememe.xyz",
      on: (chain) => {
        if (chain.id === base.id) {
          return "eip155:8453/erc20:0xc74d23c560536c7c67ad5e7a04367055e233a096";
        }
        throw new CurrencyNotSupportedError();
      },
      contractAddressOn: (chain) => {
        if (chain.id === base.id) {
          return "0xc74d23c560536c7c67ad5e7a04367055e233a096";
        }
        throw new CurrencyNotSupportedError();
      },
    },

    defaultDepositTokenDenomination: "usd",

    theme: {
      modalBorderWidth: "1px",
      modalBorderRadius: "8px",

      colorModalBackdrop: "#1B1D23",
      colorModalBackground: "#1B1D23",
      colorModalBorder: "#252835",

      buttonPrimaryForeground: "#fff",
      buttonPrimaryBackground: "#2E5DF7",
      buttonPrimaryBorderRadius: "4px",

      buttonSecondaryBackground: "#252835",
      buttonSecondaryBorderRadius: "4px",

      colorBackgroundSecondary: "#252835",

      colorTextPrimary: "#fff",
      colorTextSecondary: "#616070",

      colorAlertBackground: "#17181F",
      colorAlertAccent: "#fff",
      colorAlertBorder: "#252835",
      alertBorderWidth: "1px",

      colorAlertWarningAccent: "#E13D42",
      colorAlertWarningBackground: "#17181F",
    },
  },

  ["ham.fun"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "Ham",
    logoUrl: "https://logo.synthfinance.com/ham.fun",
    faviconUrl: "https://logo.synthfinance.com/ham.fun",
    chain: ham,
    currency: tn100x,
    withdrawChains: [base],

    defaultDepositTokenDenomination: "usd",

    theme: {
      colorModalBackdrop: "#fff",
      colorModalBorder: "#000",
      modalBorderWidth: "2px",
      modalBorderRadius: "32px",

      buttonPrimaryForeground: "#fff",
      buttonPrimaryBackground: "#eb4747",
      buttonPrimaryBorderRadius: "12px",

      buttonSecondaryBackground: "#fff",
      buttonSecondaryBorderColor: "#eb4747",
      buttonSecondaryBorderWidth: "2px",
      buttonSecondaryBorderRadius: "40px",
      buttonSecondaryPadding: "16px 20px",
    },
  },

  ["outpaint.io"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "outpaint",
    logoUrl: "https://i.imgur.com/aXPzUKn.png",
    faviconUrl: "https://logo.synthfinance.com/outpaint.io",
    chain: base,
    currency: usdc,
    payRecipient: "0xc6FfEB1298Eb33Da430d14e5Eb789256ec344625",

    theme: {
      colorModalBackground: "#F4F2EF",
      colorBackgroundSecondary: "#FFF",
      buttonSecondaryBackground: "#FFF",
      appLogoHeight: "24px",
      colorAlertBackground: "#FFF",
    },
  },

  ["supercast.xyz"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "Super",
    logoUrl: "https://www.supercast.xyz/supercast-logo-black.png",
    faviconUrl: "https://www.supercast.xyz/favicon.ico",
    chain: base,
    currency: usdc,
    payRecipient: "0xc6FfEB1298Eb33Da430d14e5Eb789256ec344625",

    theme: {
      buttonSecondaryBackground: "#F8F9FA",
    },
  },

  ["sandbox.game"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "The Sandbox",
    logoUrl:
      "https://www.sandbox.game/cdn-cgi/image/f=auto,origin-auth=share-publicly,onerror=redirect,w=125,h=30/img/01_Top_Bar/TSB-Logo-White.svg",
    faviconUrl: "https://www.sandbox.game/favicon-96x96.png",
    chain: polygon,
    currency: sand,

    theme: {
      modalBorderWidth: "1px",
      modalBorderRadius: "10px",

      buttonPrimaryForeground: "#FFFFFF",
      buttonPrimaryBackground: "#157AFF",
      buttonPrimaryBorderColor: "#157AFF",
      buttonPrimaryBorderWidth: "0px",
      buttonPrimaryBorderRadius: "20px",
      buttonPrimaryPadding: "12px 24px",

      buttonSecondaryBackground: "#22252A",
      buttonSecondaryBorderColor: "#3A3D44",
      buttonSecondaryBorderWidth: "1px",
      buttonSecondaryBorderRadius: "10px",
      buttonSecondaryPadding: "10px 20px",

      alertBackground: "#22252A",
      alertBorderWidth: "1px",

      colorModalBackdrop: "rgba(0, 0, 0, 0.75)",
      colorModalBackground: "#121212",
      colorModalBorder: "#22252A",
      colorTextPrimary: "#FFFFFF",
      colorTextSecondary: "#8E8E8E",
      colorPrimaryButtonBackground: "#157AFF",
      colorBackgroundSecondary: "#1A1D22",
      colorAlertBackground: "#22252A",
      colorAlertAccent: "#F0B90B",
      colorAlertWarningBackground: "#2E1F00",
      colorAlertWarningAccent: "#F0B90B",
      colorAlertBorder: "#3A3D44",

      appLogoHeight: "30px",
    },
  },

  ["fantium.com"]: {
    projectId: "f91ec80d-acdc-44b7-b9d2-104a44e7195e", // tushar+glide-apps@walletkit.com
    name: "FANtium",
    logoUrl: "https://app.fantium.com/fantium-logo.svg",
    faviconUrl: "https://app.fantium.com/favicon.ico",
    chain: polygon,
    currency: usdc,
    theme: {
      modalBorderWidth: "1px",
      modalBorderRadius: "12px",

      buttonPrimaryForeground: "#000000",
      buttonPrimaryBackground: "#C8FA64",
      buttonPrimaryBorderColor: "#C8FA64",
      buttonPrimaryBorderWidth: "0px",
      buttonPrimaryBorderRadius: "999px",
      buttonPrimaryPadding: "14px 20px",

      buttonSecondaryBackground: "#222222",
      buttonSecondaryBorderColor: "#333333",
      buttonSecondaryBorderWidth: "1px",
      buttonSecondaryBorderRadius: "12px",
      buttonSecondaryPadding: "10px 16px",

      alertBackground: "#1E1E1E",
      alertBorderWidth: "0px",

      colorModalBackdrop: "rgba(0, 0, 0, 0.7)",
      colorModalBackground: "#121212",
      colorModalBorder: "#333333",
      colorTextPrimary: "#FFFFFF",
      colorTextSecondary: "#B0B0B0",
      colorPrimaryButtonBackground: "#C8FA64",
      colorBackgroundSecondary: "#1E1E1E",
      colorAlertBackground: "#1E1E1E",
      colorAlertAccent: "#C8FA64",
      colorAlertWarningBackground: "#1E1E1E",
      colorAlertWarningAccent: "#C8FA64",
      colorAlertBorder: "#1E1E1E",

      appLogoHeight: "24px",
    },
  },
} as Record<string, AppConfig>;
